import { clone, isArray, isNumber, orderBy, unionBy, uniqWith } from 'lodash';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  mergeWith,
  of,
  scan,
  startWith,
  Subject,
  switchMap,
} from 'rxjs';

import {
  CommentIssue,
  IssueCommentReply,
  IssueCommentWSMessageResponse
} from 'common-module/src/components/Epic/Model/comment';
import { Member } from 'common-module/src/components/Epic/Model/member';
import { usePromise } from '@issue-detail/utils/usePromise';
import { useSocket } from '@issue-detail/utils/useSocket';
import { useVuex } from '@issue-detail/utils/useVuex';
import { fromRef } from 'common-module/src/util/fromRef';
import { computed, ref, Ref, watch } from '@vue/composition-api';
import { useObservable } from '@vueuse/rxjs';

import { getCommentList } from '../Api/comment';
import { ActionStore } from './action';
import { FieldsStore } from './fields';
import { IssueStore } from './issue';
import { LayoutStore } from './layout';

export const useComment = (
  actionStore: ActionStore,
  issueStore: IssueStore,
  fieldStore: FieldsStore,
  layoutStore: LayoutStore,
  isVisitor: Ref<boolean>
) => {
  const vuex = useVuex();
  const socket = useSocket();
  const order = ref<'asc' | 'desc'>('desc');

  actionStore.onClose(({ action }) => {
    if (action?.type === 'Edit' && !isVisitor.value) {
      socket.emit('leave', `ISSUE_${action.issueId}`);
    }
  });

  watch([actionStore.action, issueStore.issueAssemble], ([action, issue]) => {
    if (
      action?.type === 'Edit' &&
      !isVisitor.value &&
      issue?.issueVO?.projectId
    ) {
      socket.emit('join', `ISSUE_${action.issueId}`);

      refresh(action.issueId, issue.issueVO.projectId);
      order.value = 'desc';

      //TODO: 优化
      localAddedTotal.value = 0;
    }
  });


  const localMessages = new Subject<IssueCommentWSMessageResponse>();
  const onReceiveCommentLocalMessage = (msg: IssueCommentWSMessageResponse) => {
    if (actionStore.action.value?.type === 'Edit') {
      msg.result.linkId = actionStore.action.value.issueId;
      msg.local = true;
      localMessages.next(msg);
    }
  };

  const receiveCommentMessages = useObservable(
    combineLatest([
      fromRef(actionStore.action),
    ]).pipe(
      switchMap(([action]) => {
        if (action?.type === 'Edit' && !isVisitor.value) {
          return socket
            .on$<IssueCommentWSMessageResponse>('global:comment')
            .pipe(
              mergeWith(localMessages.asObservable()),
              filter((msg) => {
                return !!(
                  msg?.result &&
                  ['Issue.comment'].includes(msg.msgType) &&
                  ['reply', 'add', 'update', 'delete'].includes(msg.action)
                );
              }),
              filter((msg) => {
                return (
                  msg.action === 'delete' || // 删除消息没有 linkId
                  msg.result.linkId === action.issueId
                );
              }),
              distinctUntilChanged((a, b) => {
                if (a.action === 'update' || b.action === 'update') {
                  return false;
                }
                if (a.action === b.action) {
                  return a.result.commentId === b.result.commentId;
                }

                return false;
              }),
              scan((acc, res) => {
                if (res) {
                  if (!res.result.deleteFlag) {
                    res.result.deleteFlag = 0;
                  }
                  if (!res.result.stickerVOList) {
                    res.result.stickerVOList = [];
                  }
                  if (!res.result.favourUserList) {
                    res.result.favourUserList = [];
                  }
                  if (!res.result.files) {
                    res.result.files = [];
                  }
                  if (!res.result.subReplies) {
                    res.result.subReplies = [];
                  }

                  res.result.stickerVOList.forEach((sticker) => {
                    if (!Reflect.has(sticker, 'containsSelf')) {
                      sticker.containsSelf = sticker.replyUserList.some(
                        (u) => u.id === vuex.state.user.account.userId
                      );
                    }
                  });

                  if (!Reflect.has(res.result, 'favour')) {
                    res.result.favour = res.result.favourUserList.some(
                      (u) => u.id === vuex.state.user.account.userId
                    )
                      ? 1
                      : 0;
                  }

                  // 去重
                  if (['reply', 'add', 'delete'].includes(res.action)) {
                    const index = acc.findIndex(
                      (c) =>
                        res.result.commentId === c.result.commentId &&
                        res.action === c.action
                    );
                    if (index !== -1) {
                      if (
                        res.result.updateTime > acc[index].result.updateTime
                      ) {
                        acc[index] = res;
                      }
                      return acc;
                    }
                  }

                  acc.push(res);
                }

                return acc;
              }, [] as IssueCommentWSMessageResponse[]),
              startWith([] as IssueCommentWSMessageResponse[])
            );
        }
        return of([]);
      })
    ),
    {
      initialValue: [] as IssueCommentWSMessageResponse[]
    }
  );

  const {
    result: originCommentResponse,
    refresh,
    onReceive: onCommentsReceive
  } = usePromise(
    async (issueId: number, projectId: number) => {
      return getCommentList(issueId, projectId);
    },
    null,
    { immediate: false, clearOnRefresh: false }
  );

  const compositionComments = computed(() => {
    const addedComments = receiveCommentMessages.value
      .filter((msg) => msg.action === 'add')
      .map((msg) => msg.result);

    const addedReplies = receiveCommentMessages.value
      .filter((msg) => msg.action === 'reply')
      .map((msg) => msg.result as unknown as IssueCommentReply);

    const deleteComment = receiveCommentMessages.value
      .filter((msg) => msg.action === 'delete')
      .map((msg) => msg.result.commentId);

    const modifiedCommentMap = receiveCommentMessages.value.reduce(
      (map, msg) => {
        if (msg.action === 'update') {
          map.set(msg.result.commentId, msg.result);
        }
        return map;
      },
      new Map<number, CommentIssue>()
    );

    const commentIdRecord: number[] = [];

    return orderBy(
      [...addedComments, ...(originCommentResponse.value?.list || [])]
        .reduce((arr, comment) => {
          const modifiedComment =
            modifiedCommentMap.get(comment.commentId) || comment;

          // 排序的目的是为了保证回复的回复在回复的后面
          const allReplies = orderBy(
            addedReplies,
            (r) => r.updateTime,
            'asc'
          ).reduce((arr, reply) => {
            if (
              reply.replyId === comment.commentId ||
              arr.some((r) => r.commentId === reply.replyId)
            ) {
              arr.push(reply);
            }
            return arr;
          }, clone(comment.subReplies));

          const subReplies = orderBy(
            allReplies.reduce((subArr, subComment) => {
              const modifiedSubComment =
                modifiedCommentMap.get(subComment.commentId) || subComment;
              if (modifiedSubComment) {
                subArr.push({
                  ...modifiedSubComment
                } as IssueCommentReply);
              }
              return subArr;
            }, [] as IssueCommentReply[]),
            'createTime',
            'asc'
          );

          if (deleteComment.includes(modifiedComment.commentId)) {
            modifiedComment.deleteFlag = 1;
          }
          subReplies.forEach((reply) => {
            if (deleteComment.includes(reply.commentId)) {
              reply.deleteFlag = 1;
            }
          });
          arr.push({
            ...modifiedComment,
            subReplies: uniqWith(subReplies.filter(
              (reply) =>
                reply.deleteFlag === 0 ||
                subReplies.some((r) => r.replyId === reply.commentId)
            ), (a, b) => a.commentId === b.commentId)
          });
          return arr;
        }, [] as CommentIssue[])
        .filter(
          comment => {
            if (commentIdRecord.includes(comment.commentId)) {
              return false;
            }
            commentIdRecord.push(comment.commentId);
            const isAllSubRepliesDeleted = comment.subReplies.length ? comment.subReplies.every(r => r.deleteFlag === 1) : true;
            if (comment.deleteFlag === 1 && isAllSubRepliesDeleted) {
              return false;
            }
            return comment.deleteFlag === 0 || comment.subReplies.length;
          }
        ),
      'createTime',
      order.value
    );
  });

  // TODO: 优化
  const localAddedTotal = ref(0);

  const total = computed(() =>
    compositionComments.value.reduce((acc, comment) => {
      if (comment.deleteFlag === 0) {
        acc += 1;
      }
      acc += comment.subReplies.filter(
        (reply) => reply.deleteFlag === 0
      ).length;
      return acc;
    }, localAddedTotal.value)
  );

  const MAX_Recommended_COUNT = 5;
  const getRecommendedMembers = (
    commentId?: number,
    skipUserId: number[] = []
  ) => {
    if (
      actionStore.action.value?.type === 'Edit' &&
      issueStore.issueAssemble.value &&
      fieldStore.members.value
    ) {
      const result = new Set<number | null | undefined>();

      unionBy(
        orderBy(
          compositionComments.value.reduce((acc, comment) => {
            if (comment.deleteFlag === 0) {
              acc.push({
                user: comment.commentUser.userId,
                time: comment.updateTime
              });

              comment.commentAttributes?.commentAtInfos.forEach((m, i) => {
                acc.push({
                  user: m.userId,
                  time: comment.updateTime + i
                });
              });
            }

            comment.subReplies.forEach((reply) => {
              if (reply.deleteFlag === 0) {
                acc.push({
                  user: reply.commentUser.userId,
                  time: reply.updateTime
                });

                reply.commentAttributes?.commentAtInfos.forEach((m, i) => {
                  acc.push({
                    user: m.userId,
                    time: comment.updateTime + i
                  });
                });
              }
            });

            return acc;
          }, [] as Array<{ user: number; time: number }>),
          'time',
          'desc'
        ).map((c) => c.user)
      ).forEach((u) => result.add(u));

      result.add(fieldStore.fieldValues.value.assignee);
      result.add(fieldStore.fieldValues.value.owner);
      result.add(issueStore.issueAssemble.value.issueVO.data.createBy);
      result.add(issueStore.issueAssemble.value.issueVO.data.updateBy);

      if (isArray(fieldStore.fieldValues.value.follows)) {
        fieldStore.fieldValues.value.follows.forEach((id) => {
          result.add(id);
        });
      }

      layoutStore.allFields.value.forEach((field) => {
        if (field.controlCode === 'member') {
          const value = fieldStore.fieldValues.value[field.fieldCode];
          if (value) {
            if (isArray(value)) {
              value.forEach((id) => {
                result.add(id);
              });
            }
            if (isNumber(value)) {
              result.add(value);
            }
          }
        }
      });

      return Array.from(result)
        .map((id) => {
          return id
            ? (fieldStore.members.value || []).find(
              (member) => member.userId === id
            )
            : null;
        })
        .filter((m) => {
          return (
            m &&
            m.enabled &&
            ![...skipUserId, vuex.state.user.account.userId].includes(m.userId)
          );
        })
        .slice(0, MAX_Recommended_COUNT) as Member[];
    }
    return [];
  };

  return {
    receiveCommentMessages,
    getRecommendedMembers,
    compositionComments,
    commentOrder: order,
    commentTotal: total,
    onCommentsReceive,
    refreshCommentTotal: () => {
      localAddedTotal.value++;
    },
    refreshComments: refresh,
    onReceiveCommentLocalMessage
  };
};

export type CommentStore = ReturnType<typeof useComment>;
